/**
 * 拆分模型或摘要里合并在一行的合同当事方（如「甲方：A 乙方 B」）。
 */

export type CombinedPartyEntry = {
  role: string;
  name: string;
};

const PARTY_ROLE = '(?:甲|乙|丙|丁)\\s*方|发包[人方]|承包[人方]|监理[人方单位]?|委托方|受托方|买方|卖方|出租方|承租方|担保人|被担保人';

/**
 * 去掉括注与多余空白，保留公司主体名称。
 * @param value 原始名称片段
 */
export function cleanSimplePartyName(value: string): string {
  return String(value ?? '')
    .replace(/[（(][^）)]{0,40}[）)]/gu, '')
    .replace(/[，,。；;：:]/gu, ' ')
    .replace(/\s+/gu, ' ')
    .trim()
    .slice(0, 80);
}

function normalizePartyRole(raw: string): string {
  return String(raw ?? '').replace(/\s+/gu, '').trim();
}

/**
 * 将合并在一行的当事方拆成多条（支持分号或空格分隔的「乙方」等）。
 * @param raw 原始文本
 */
export function splitCombinedPartyEntries(raw: string): CombinedPartyEntry[] {
  const source = String(raw ?? '').trim();
  if (!source) return [];
  const roleRe = new RegExp(PARTY_ROLE, 'u');
  if (!roleRe.test(source)) {
    const name = cleanSimplePartyName(source);
    return name.length >= 2 ? [{role: '', name}] : [];
  }
  let pieces = source
    .split(/[；;]\s*/u)
    .map(item => item.trim())
    .filter(Boolean);
  if (pieces.length <= 1) {
    pieces = source
      .split(new RegExp(`\\s+(?=(?:乙|丙|丁)\\s*方(?:\\s*[：:]|\\s+(?!\\s))|(?:(?:甲|乙|丙|丁)\\s*方|发包[人方]|承包[人方]|监理[人方单位]?)\\s*[：:])`, 'u'))
      .map(item => item.trim())
      .filter(Boolean);
  }
  if (pieces.length <= 1) {
    pieces = [source];
  }
  const entries: CombinedPartyEntry[] = [];
  const pieceRe = new RegExp(`^(${PARTY_ROLE})\\s*[：:]?\\s*(.+)$`, 'u');
  for (const piece of pieces) {
    const matched = piece.match(pieceRe);
    if (matched) {
      const name = cleanSimplePartyName(matched[2]);
      if (name.length >= 2) {
        entries.push({role: normalizePartyRole(matched[1]), name});
      }
      continue;
    }
    const name = cleanSimplePartyName(piece);
    if (name.length >= 2) entries.push({role: '', name});
  }
  return entries;
}

/**
 * 仅返回拆分后的企业名称列表。
 * @param raw 原始文本
 */
export function splitCombinedPartyName(raw: string): string[] {
  return splitCombinedPartyEntries(raw).map(item => item.name).filter(Boolean);
}
